import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { principlesData } from '../data/mock';

/**
 * PrinciplesSection Component
 * 
 * Section "Nos Principes" avec citations animées.
 * Chaque principe apparaît avec un fade-in décalé au scroll.
 */
const PrinciplesSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: false, margin: '-15%' });
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };
  
  const quoteVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: 'spring',
        damping: 25,
        stiffness: 100,
      },
    },
  };
  
  return (
    <section
      ref={sectionRef}
      className="section-container bg-[#1A1A1A]"
    >
      {/* Section Header */}
      <motion.div
        className="text-center mb-16"
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <p className="text-[#2A3B8F] uppercase tracking-widest text-sm mb-4">
          Ce que nous croyons
        </p>
        <h2 className="heading-lg mb-6">
          Nos principes
        </h2>
      </motion.div>
      
      {/* Principles List */}
      <motion.div
        className="max-w-4xl mx-auto space-y-6"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
      >
        {principlesData.map((principle, index) => (
          <motion.blockquote
            key={index}
            variants={quoteVariants}
            className="relative p-6 md:p-8 text-lg md:text-xl italic text-[#F8F8F8] border-l-2 border-[#2A3B8F] bg-gradient-to-r from-[#2A3B8F]/5 to-transparent group interactive"
            whileHover={{
              x: 10,
              borderLeftWidth: '4px',
              transition: { type: 'spring', stiffness: 300 },
            }}
          >
            <span className="relative">
              {principle}
            </span>
            
            {/* Quote number */}
            <motion.span
              className="absolute top-4 right-4 text-4xl font-bold text-[#2A3B8F]/10"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.5 + index * 0.1 }}
            >
              {String(index + 1).padStart(2, '0')}
            </motion.span>
          </motion.blockquote>
        ))}
      </motion.div>
      
      {/* Decorative Element */}
      <motion.div
        className="flex justify-center mt-16"
        initial={{ opacity: 0, scale: 0 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ delay: 1, type: 'spring' }}
      >
        <div className="flex items-center gap-4">
          <div className="w-16 h-px bg-[#333333]" />
          <motion.div
            className="w-3 h-3 rotate-45 border border-[#2A3B8F]"
            animate={{ rotate: [45, 225, 45] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
          />
          <div className="w-16 h-px bg-[#333333]" />
        </div>
      </motion.div>
    </section>
  );
};

export default PrinciplesSection;
